// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Sa2 } from '../types';
import { num, people, esc, REMOTENESS_SHORT, REMOTENESS_ORDER, STATE_ORDER } from '../format';
import { gloss } from '../glossary';
import { openRegion } from '../main';

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '');

type Sort = 'pop' | 'lost' | 'name';

export function renderDeserts(root: HTMLElement, data: Dataset): void {
  const { deserts, regions, meta } = data;
  const c = meta.counts;
  const nameToCode = new Map(regions.map((r) => [norm(r.name), r.code]));

  const lost = deserts.filter((d) => d.lostLast);
  let stateF = 'all';
  let remF = 'all';
  let netF: 'all' | 'bankpost' | 'nothing' = 'all';
  let sort: Sort = 'pop';
  let query = '';

  root.innerHTML = `
    <div class="view-head">
      <h2>Banking deserts</h2>
      <p>A ${gloss('lostlast', 'town that had a bank branch in ' + meta.firstYear + ' and has none today')}. There are <strong>${num(c.sa2LostLastCount)}</strong> of them, home to about <strong>${people(c.popAffected)}</strong> people. For most, ${gloss('bankpost', 'Bank@Post')} is the last counter standing — but ${num(c.sa2LostLastNothing)} have no staffed banking at all.</p>
    </div>

    <div class="stat-row">
      <div class="stat hl"><div class="stat-label">Towns that lost their last branch</div><div class="stat-value">${num(c.sa2LostLastCount)}</div><div class="stat-note">since ${meta.firstYear}</div></div>
      <div class="stat"><div class="stat-label">People living in them</div><div class="stat-value">${people(c.popAffected)}</div><div class="stat-note">ABS resident population</div></div>
      <div class="stat"><div class="stat-label">Still have a Bank@Post</div><div class="stat-value">${num(c.sa2LostLastWithBankpost)}</div><div class="stat-note">${((c.sa2LostLastWithBankpost / c.sa2LostLastCount) * 100).toFixed(0)}% of deserts</div></div>
      <div class="stat hl"><div class="stat-label">No staffed banking left</div><div class="stat-value">${num(c.sa2LostLastNothing)}</div><div class="stat-note">no branch, no Bank@Post</div></div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>The safety net</h3><p>Of every town that lost its last branch, how many still have a Bank@Post counter to fall back on.</p></div>
      <div style="display:flex;height:34px;border-radius:6px;overflow:hidden;border:1px solid var(--border-subtle)">
        <div data-tip="${esc(`${num(c.sa2LostLastWithBankpost)} towns keep a Bank@Post`)}" style="flex:${c.sa2LostLastWithBankpost};background:var(--bankpost);color:#fff;display:flex;align-items:center;justify-content:center;font-size:var(--font-size-sm);font-weight:600">${num(c.sa2LostLastWithBankpost)} keep Bank@Post</div>
        <div data-tip="${esc(`${num(c.sa2LostLastNothing)} towns have nothing`)}" style="flex:${c.sa2LostLastNothing};background:var(--lost);color:#fff;display:flex;align-items:center;justify-content:center;font-size:var(--font-size-sm);font-weight:600">${num(c.sa2LostLastNothing)} nothing</div>
      </div>
    </div>

    <div class="panel">
      <div class="controls">
        <input type="search" id="q" placeholder="Search a town…" aria-label="Search town" />
        <select id="state"><option value="all">All states</option>${STATE_ORDER.map((s) => `<option value="${s}">${s}</option>`).join('')}</select>
        <select id="rem"><option value="all">All remoteness</option>${REMOTENESS_ORDER.map((r) => `<option value="${esc(r)}">${esc(REMOTENESS_SHORT[r])}</option>`).join('')}</select>
        <div class="seg" aria-label="Safety net">
          <button data-net="all" aria-pressed="true">All</button>
          <button data-net="bankpost" aria-pressed="false">Has Bank@Post</button>
          <button data-net="nothing" aria-pressed="false">Nothing left</button>
        </div>
        <div class="seg" aria-label="Sort">
          <button data-sort="pop" aria-pressed="true">Most people</button>
          <button data-sort="lost" aria-pressed="false">Most lost</button>
          <button data-sort="name" aria-pressed="false">A–Z</button>
        </div>
      </div>
      <div class="table-scroll">
        <table>
          <thead><tr>
            <th>Town</th><th>State</th><th>Remoteness</th>
            <th class="right">Branches ${meta.firstYear}</th><th class="right">Now</th>
            <th class="right">Population</th><th>What's left</th>
          </tr></thead>
          <tbody id="rows"></tbody>
        </table>
      </div>
      <p class="mini-note" id="count"></p>
    </div>
  `;

  const rowsEl = root.querySelector('#rows') as HTMLElement;
  const countEl = root.querySelector('#count') as HTMLElement;

  function whatsLeft(d: Sa2): string {
    if (d.bankpostNow > 0) return `<span class="tag bankpost">Bank@Post${d.atmNow > 0 ? ' + ATM' : ''}</span>`;
    if (d.atmNow > 0) return `<span class="tag nothing">ATM only</span>`;
    return `<span class="tag nothing">Nothing</span>`;
  }

  function draw(): void {
    let rows = lost.filter((d) =>
      (stateF === 'all' || d.state === stateF) &&
      (remF === 'all' || d.remoteness === remF) &&
      (netF === 'all' || (netF === 'bankpost' ? d.bankpostNow > 0 : d.bankpostNow === 0 && d.atmNow === 0)) &&
      (!query || norm(d.name).includes(norm(query))),
    );
    rows.sort((a, b) =>
      sort === 'name' ? a.name.localeCompare(b.name)
      : sort === 'lost' ? b.branchLost - a.branchLost || (b.pop || 0) - (a.pop || 0)
      : (b.pop || 0) - (a.pop || 0) || b.branchLost - a.branchLost);

    rowsEl.innerHTML = rows.slice(0, 400).map((d) => {
      const code = nameToCode.get(norm(d.sa3));
      return `<tr class="clickable" data-code="${esc(code ?? '')}">
        <td class="region-name">${esc(d.name)}</td>
        <td><span class="state-pill">${esc(d.state)}</span></td>
        <td>${d.remoteness ? esc(REMOTENESS_SHORT[d.remoteness] ?? d.remoteness) : '—'}</td>
        <td class="right num">${num(d.branch2017)}</td>
        <td class="right num">0</td>
        <td class="right num">${d.pop == null ? '—' : num(d.pop)}</td>
        <td>${whatsLeft(d)}</td>
      </tr>`;
    }).join('');

    const shownPop = rows.reduce((a, d) => a + (d.pop || 0), 0);
    countEl.textContent = `${rows.length} towns${rows.length > 400 ? ' (showing first 400)' : ''} · ~${people(shownPop)} residents. Click a town to open its wider region.`;

    rowsEl.querySelectorAll<HTMLElement>('tr[data-code]').forEach((tr) =>
      tr.addEventListener('click', () => { const code = tr.dataset.code; if (code) openRegion(code); }),
    );
  }

  (root.querySelector('#q') as HTMLInputElement).addEventListener('input', (e) => {
    query = (e.target as HTMLInputElement).value; draw();
  });
  (root.querySelector('#state') as HTMLSelectElement).addEventListener('change', (e) => { stateF = (e.target as HTMLSelectElement).value; draw(); });
  (root.querySelector('#rem') as HTMLSelectElement).addEventListener('change', (e) => { remF = (e.target as HTMLSelectElement).value; draw(); });
  root.querySelectorAll<HTMLButtonElement>('[data-net]').forEach((b) =>
    b.addEventListener('click', () => { netF = b.dataset.net as typeof netF; root.querySelectorAll<HTMLButtonElement>('[data-net]').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); draw(); }),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-sort]').forEach((b) =>
    b.addEventListener('click', () => { sort = b.dataset.sort as Sort; root.querySelectorAll<HTMLButtonElement>('[data-sort]').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); draw(); }),
  );

  draw();
}
